import React, { useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Topbar from "../Global/topbar";
import CachedIcon from "@mui/icons-material/Cached";
import {
  Avatar,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { getCallRecordings } from "../../services/api/callRecordings";
import { Context } from "../../context/contextapi";
import "../Audio Type/audio.css";
import "./callRecordings.css";

const formatDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Recording duration is recorded in seconds (HMS webhook). Render as mm:ss.
const formatDuration = (secStr) => {
  const sec = Number(secStr);
  if (!sec || Number.isNaN(sec)) return "-";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
};

const formatSize = (bytesStr) => {
  const bytes = Number(bytesStr);
  if (!bytes || Number.isNaN(bytes)) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const PartyCell = ({ name, photo, phone, role }) => (
  <div className="recording-party">
    <Avatar
      src={photo || undefined}
      alt={name || "?"}
      sx={{ width: 36, height: 36 }}
    >
      {(name || "?").slice(0, 1).toUpperCase()}
    </Avatar>
    <div className="recording-party-text">
      <div className="recording-party-name">{name || "-"}</div>
      <div className="recording-party-meta">
        <Chip
          label={role}
          size="small"
          variant="outlined"
          sx={{ height: 18, fontSize: "0.65rem" }}
        />
        {phone ? <span className="recording-party-phone">{phone}</span> : null}
      </div>
    </div>
  </div>
);

export default function CallRecordings() {
  const [loader, setLoader] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [APIdata, setAPIdata] = useState([]);
  const [reload, setReload] = useState(true);
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const start_key = useRef({ 1: null });
  const { setHasmore } = useContext(Context);

  const fetchRecordings = async () => {
    setLoader(true);
    setNotFound(false);
    const paginationKey = start_key.current[page];
    try {
      const response = await getCallRecordings(limit, paginationKey);
      if (response?.data?.status === "success") {
        const result = response?.data?.data;
        const rows = result?.data || [];
        if (rows.length === 0) {
          setAPIdata([]);
          setNotFound(true);
          setHasmore(false);
        } else {
          setAPIdata(rows);
          start_key.current[page + 1] = result?.start_key || null;
          setHasmore(Boolean(result?.start_key));
        }
      } else {
        setNotFound(true);
        setHasmore(false);
      }
    } catch {
      setNotFound(true);
      setHasmore(false);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchRecordings();
  }, [reload, page, limit]);

  const handleReload = () => {
    start_key.current = { 1: null };
    setPage(1);
    setReload((p) => !p);
  };

  const handlePrev = () => {
    if (page > 1) setPage((p) => p - 1);
  };

  const handleNext = () => {
    if (start_key.current[page + 1]) setPage((p) => p + 1);
  };

  return (
    <div className="audio-main">
      <div className="audio-user-top">
        <Topbar />
        <div className="top-mid-audio">
          <h1>Call Recordings</h1>
          <Link to="/admin/call_recordings">Call Recordings</Link> &#62;
        </div>
        <div className="tob-btm-audio">
          <div className="button-box-user" />
          <div>
            <button onClick={handleReload}>
              <CachedIcon />
            </button>
          </div>
        </div>

        <div className="recordings-table-wrap">
          {loader ? (
            <div className="recordings-loader">
              <CircularProgress size={28} />
            </div>
          ) : notFound ? (
            <div className="recordings-empty">No call recordings found.</div>
          ) : (
            <TableContainer component={Paper} className="recordings-table">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Caller</TableCell>
                    <TableCell>Callee</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Size</TableCell>
                    <TableCell>Mode</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Recording</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {APIdata.map((row, idx) => (
                    <TableRow key={row.id || row.call_log_id || idx}>
                      <TableCell>
                        {(page - 1) * limit + idx + 1}
                      </TableCell>
                      <TableCell>
                        <PartyCell
                          name={row.caller_name}
                          photo={row.caller_photo}
                          phone={row.caller_phone}
                          role={row.caller_entity || "-"}
                        />
                      </TableCell>
                      <TableCell>
                        <PartyCell
                          name={row.callee_name}
                          photo={row.callee_photo}
                          phone={row.callee_phone}
                          role={row.callee_entity || "-"}
                        />
                      </TableCell>
                      <TableCell>
                        {formatDate(
                          row.call_start_time ||
                            row.recorded_at ||
                            row.created_at,
                        )}
                      </TableCell>
                      <TableCell>
                        {formatDuration(
                          row.recording_duration || row.call_duration,
                        )}
                      </TableCell>
                      <TableCell>{formatSize(row.recording_size)}</TableCell>
                      <TableCell>
                        <Chip
                          label={(row.call_mode || "hms").toUpperCase()}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.status || row.call_status || "-"}
                          size="small"
                          color={
                            row.status === "available" ? "success" : "default"
                          }
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {row.recording_url ? (
                          <audio
                            controls
                            preload="none"
                            className="recording"
                            src={row.recording_url}
                          />
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </div>

        <div className="recordings-footer">
          <div className="recordings-rpp">
            <span>Rows per page:</span>
            <Select
              size="small"
              value={limit}
              onChange={(e) => {
                start_key.current = { 1: null };
                setPage(1);
                setLimit(Number(e.target.value));
              }}
            >
              {[5, 10, 15, 20, 50, 100].map((n) => (
                <MenuItem key={n} value={n}>
                  {n}
                </MenuItem>
              ))}
            </Select>
          </div>
          <div className="recordings-pager">
            <button onClick={handlePrev} disabled={page === 1}>
              Prev
            </button>
            <span className="recordings-page-num">Page {page}</span>
            <button
              onClick={handleNext}
              disabled={!start_key.current[page + 1]}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
